const ENTITY_LABEL = {
  topic_weight: { label: "Peso topic", color: "bg-emerald-100 text-emerald-700" },
  pillar_weight: { label: "Pesi pilastri E/S/G", color: "bg-violet-100 text-violet-700" },
  indicator: { label: "Benchmark indicatore", color: "bg-amber-100 text-amber-700" },
  benchmark: { label: "Benchmark cluster", color: "bg-blue-100 text-blue-700" },
  assessment: { label: "Assessment", color: "bg-sky-100 text-sky-700" },
  user: { label: "Utente", color: "bg-rose-100 text-rose-700" },
  organization: { label: "Azienda", color: "bg-teal-100 text-teal-700" },
  document: { label: "Documento", color: "bg-orange-100 text-orange-700" },
  settings: { label: "Impostazioni", color: "bg-slate-200 text-slate-700" },
};

const FIELD_LABEL = { weight: "Peso", weight_e: "Peso E", weight_s: "Peso S", weight_g: "Peso G", p10: "P10", p90: "P90", p10_value: "P10", p90_value: "P90" };

const ISO_RX = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?/g;
const fmtIso = (s) => s.replace(ISO_RX, (m) => {
  const d = new Date(m);
  return Number.isNaN(d.getTime()) ? m : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
});
const isEmpty = (v) => v === null || v === undefined || v === "" || v === "—" || v === "None";

const fmtValue = (v) => {
  if (isEmpty(v)) return "—";
  if (typeof v === "number") return Number(v).toFixed(3).replace(/\.?0+$/, "");
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (Array.isArray(v)) return v.join(", ") || "—";
  if (typeof v === "object") return JSON.stringify(v);
  return fmtIso(String(v));
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

// Legacy entries (before the canonical {entity_type, changes} schema)
function deriveChanges(item) {
  if (item.changes && Object.keys(item.changes).length > 0) return item.changes;
  const p = item.payload || {};
  switch (item.action) {
    case "workflow_transition": {
      const c = { "Stato workflow": { old: p.from || "—", new: p.to || "—" } };
      if (p.note) c["Nota"] = { old: "", new: p.note };
      return c;
    }
    case "admin_unlock":
      return { "Sblocco amministrativo": { old: "—", new: "ATTIVO" }, "Motivazione sblocco": { old: "", new: p.reason || "" } };
    case "admin_lock":
      return { "Sblocco amministrativo": { old: "ATTIVO", new: "—" } };
    case "verbal_stage1":
    case "verbal_stage2":
      return { "Verbale registrato": { old: "—", new: p.audit_date || "" }, "Esito": { old: "", new: p.outcome || "" } };
    case "technical_review":
      return { "Riesame tecnico": { old: "—", new: p.decision || p.outcome || "" } };
    case "decision":
      return { "Delibera": { old: "—", new: p.decision || p.outcome || "" } };
    default:
      return {};
  }
}

function Reference({ it }) {
  if (it.organization_name) {
    return (
      <div title={`ID assessment: ${it.assessment_id}`} data-testid={`audit-ref-${it.id}`}>
        <div className="text-xs font-semibold text-slate-800">{it.organization_name}</div>
        <div className="text-[11px] text-slate-500 font-mono">{it.certificate_code || (it.assessment_year ? `Assessment ${it.assessment_year}` : "")}</div>
      </div>
    );
  }
  const ref = it.entity_key || it.assessment_id || "";
  return <div className="font-mono text-[11px] text-slate-700 max-w-[220px] truncate" title={ref} data-testid={`audit-ref-${it.id}`}>{ref || "—"}</div>;
}

export function AuditRow({ it }) {
  const label = ENTITY_LABEL[it.entity_type] || (it.assessment_id ? ENTITY_LABEL.assessment : { label: it.entity_type || it.action || "—", color: "bg-slate-100 text-slate-700" });
  const changes = Object.fromEntries(Object.entries(deriveChanges(it)).filter(([, c]) => {
    const ch = c && typeof c === "object" && "new" in c ? c : { old: "", new: c };
    if (isEmpty(ch.old) && isEmpty(ch.new)) return false;
    return JSON.stringify(ch.old) !== JSON.stringify(ch.new);
  }));
  return (
    <tr data-testid={`audit-row-${it.id}`} className="hover:bg-slate-50/50 align-top">
      <td className="px-6 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtTime(it.timestamp)}</td>
      <td className="px-6 py-3">
        <div className="font-medium text-slate-900 text-sm">{it.user_name || it.user_email?.split("@")[0] || "—"}</div>
        <div className="text-xs text-slate-400">{it.user_email} · {it.user_role}</div>
      </td>
      <td className="px-6 py-3">
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${label.color}`}>{label.label}</span>
        {it.action && <div className="text-[10px] text-slate-400 mt-1">{it.action}</div>}
      </td>
      <td className="px-6 py-3"><Reference it={it} /></td>
      <td className="px-6 py-3">
        <ul className="space-y-1">
          {Object.entries(changes).map(([field, c]) => {
            const ch = c && typeof c === "object" && "new" in c ? c : { old: "", new: c };
            const onlyNew = isEmpty(ch.old) && !isEmpty(ch.new);
            return (
              <li key={field} className="text-xs">
                <span className="font-semibold text-slate-700">{FIELD_LABEL[field] || field}:</span>{" "}
                {onlyNew ? <span className="text-viva-700 font-medium">{fmtValue(ch.new)}</span> : (
                  <><span className="text-red-600 line-through">{fmtValue(ch.old)}</span> <span className="text-slate-400">→</span> <span className="text-viva-700 font-semibold">{fmtValue(ch.new)}</span></>
                )}
              </li>
            );
          })}
          {Object.keys(changes).length === 0 && <li className="text-xs text-slate-500 italic">{it.action || "—"}</li>}
        </ul>
      </td>
    </tr>
  );
}
